import React, { useCallback, useEffect, useState } from "react";
import {
  getRandomHour,
  SCHEDULE_END_TYPES,
  SCHEDULE_TYPES,
  convertSchedulerDataToText,
  type ScheduleType,
  type ScheduleEndType,
  type SchedulerData,
  type SchedulerRunnerData,
} from "./SchedulerUtils";
import TypeSelection, {
  type TypeSelectionProps,
} from "./default-components/TypeSelection";
import HourSelection, {
  type HourSelectionProps,
} from "./default-components/HourSelection";
import DailyRunner from "./default-components/DailyRunner.js";
import WeeklyRunner from "./default-components/WeeklyRunner.js";
import MonthlyRunner from "./default-components/MonthlyRunner.js";
import ScheduleEnd, {
  type ScheduleEndChange,
} from "./default-components/ScheduleEnd.js";
import "./Scheduler.scss";
import FieldViewBlock from "../FieldViewBlock/FieldViewBlock";

// TODO: DailyRunner/WeeklyRunner/MonthlyRunner are still JS (converted in
// parallel). Type the runner boundary locally until they are converted.
interface RunnerProps {
  onChange: (data: SchedulerRunnerData) => void;
  data?: SchedulerRunnerData["data"];
  HourSelectionComponent: React.ComponentType<HourSelectionProps>;
  disableInterval?: boolean;
  hideInterval?: boolean;
  disabled?: boolean;
}

const mapper = {
  [SCHEDULE_TYPES.DAY]: DailyRunner,
  [SCHEDULE_TYPES.WEEK]: WeeklyRunner,
  [SCHEDULE_TYPES.MONTH]: MonthlyRunner,
} as Record<ScheduleType, React.ComponentType<RunnerProps>>;

// `ends` holds either the initial ScheduleEndType string or the object payload
// emitted by ScheduleEnd's onChange (which carries a `.type`).
type ScheduleEndState =
  | ScheduleEndType
  | Extract<ScheduleEndChange, { type: ScheduleEndType }>;

const randomHour = getRandomHour();

const defaultDataForRunner = {
  [SCHEDULE_TYPES.DAY]: {
    type: SCHEDULE_TYPES.DAY,
    data: {
      interval: 1,
      times: [randomHour],
    },
  },
  [SCHEDULE_TYPES.WEEK]: {
    type: SCHEDULE_TYPES.WEEK,
    data: {
      interval: 1,
      times: [randomHour],
      selectedDays: ["monday"],
    },
  },
  [SCHEDULE_TYPES.MONTH]: {
    type: SCHEDULE_TYPES.MONTH,
    data: {
      interval: 1,
      times: [randomHour],
      selectedDays: [1],
    },
  },
} as Record<ScheduleType, SchedulerRunnerData>;

export interface SchedulerChangeValue {
  type: ScheduleType;
  runnerData: SchedulerRunnerData;
  // Mirrors the `ends` state: a ScheduleEndType string initially, or the
  // ScheduleEnd onChange payload object once an end is selected.
  ends: ScheduleEndState;
}

export interface SchedulerProps {
  disabled?: boolean;
  HourSelectionComp?: React.ComponentType<HourSelectionProps>;
  TypeSelectionComp?: React.ComponentType<TypeSelectionProps>;
  allowedTypes?: ScheduleType[];
  allowedEndTypes?: ScheduleEndType[];
  hideEnds?: boolean;
  data?: SchedulerData;
  onChange: (value: SchedulerChangeValue) => void;
  disableInterval?: boolean;
  hideInterval?: boolean;
  viewMode?: boolean;
  label?: string;
  // Accepted but unused (CollectionSettings passes it); the original JS
  // silently ignored it, so it is intentionally not destructured below.
  required?: boolean;
  "data-testid"?: string;
}

const Scheduler: React.FC<SchedulerProps> = ({
  disabled = false,
  HourSelectionComp = HourSelection,
  TypeSelectionComp = TypeSelection,
  allowedTypes = [
    SCHEDULE_TYPES.DAY,
    SCHEDULE_TYPES.WEEK,
    SCHEDULE_TYPES.MONTH,
  ],
  allowedEndTypes = [
    SCHEDULE_END_TYPES.NEVER,
    SCHEDULE_END_TYPES.DATE,
    SCHEDULE_END_TYPES.AFTER,
  ],
  hideEnds = false,
  data,
  onChange,
  disableInterval = false,
  hideInterval = false,
  viewMode = false,
  label = "",
  "data-testid": dataTestId = "",
}) => {
  const [selectedType, setSelectedType] = useState<ScheduleType>(
    data?.type || allowedTypes?.[0] || SCHEDULE_TYPES.DAY
  );
  const [runnerData, setRunnerData] = useState<SchedulerRunnerData>(
    data?.runnerData || defaultDataForRunner[selectedType]
  );
  // `ends` is initialized to a ScheduleEndType string, but scheduleEndOnChange
  // stores the ScheduleEnd onChange payload, which is an object carrying a
  // `.type`. Type it as the union so the `ends?.type` read below typechecks.
  const [ends, setEnds] = useState<ScheduleEndState>(
    data?.ends || SCHEDULE_END_TYPES.NEVER
  );

  useEffect(() => {
    onChange({
      type: selectedType,
      runnerData,
      ends: ends,
    });
  }, []);

  const scheduleEndOnChange = useCallback(
    (data: ScheduleEndChange) => {
      setEnds(data as ScheduleEndState);
      onChange({
        type: selectedType,
        runnerData,
        ends: data as ScheduleEndState,
      });
    },
    [onChange, runnerData, selectedType]
  );

  const Runner = mapper[selectedType];
  return !viewMode ? (
    <div className="scheduler-wrapper" data-testid={`${dataTestId}-scheduler`}>
      {!!label && <label className={`input-label`}>{label}</label>}
      <TypeSelectionComp
        label="Run every"
        selectedType={selectedType}
        onChange={(data: ScheduleType) => {
          setSelectedType(data);
          setRunnerData(defaultDataForRunner[data]);
          onChange({
            type: data,
            runnerData: defaultDataForRunner[data],
            ends,
          });
        }}
        allowedTypes={allowedTypes}
        disabled={disabled}
      />
      {!!Runner && (
        <Runner
          onChange={(data: SchedulerRunnerData) => {
            setRunnerData(data);
            onChange({
              type: selectedType,
              runnerData: data,
              ends,
            });
          }}
          data={runnerData?.data}
          HourSelectionComponent={HourSelectionComp}
          disableInterval={disableInterval}
          hideInterval={hideInterval}
          disabled={disabled}
        />
      )}
      {!hideEnds && (
        <ScheduleEnd
          allowedTypes={allowedEndTypes}
          value={ends as number | string}
          onChange={scheduleEndOnChange}
          // `ends` is a ScheduleEndType string initially (`.type` is undefined,
          // so this falls back to NEVER) or the onChange payload object once an
          // end is picked (read its `.type`). Matches the original JS exactly.
          type={
            (ends as Extract<ScheduleEndState, { type: ScheduleEndType }>)
              ?.type || SCHEDULE_END_TYPES.NEVER
          }
          disabled={disabled}
        />
      )}
    </div>
  ) : (
    <FieldViewBlock
      label={label}
      data-testid={`${dataTestId}-scheduler`}
      text={convertSchedulerDataToText({ data })}
    />
  );
};

export default Scheduler;
